/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {
  Component,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import {UserService} from '../../../services/user.service';
import {IxActiveModal, ModalService, ToastService} from '@siemens/ix-angular';
import {ConnectorService} from '../../../services/connector.service';
import {TranslateService} from '@ngx-translate/core';
import {Subscription} from 'rxjs';
import {ConnectorConfig} from '../../../models/ConnectorConfig';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {CustomValidators} from '../../../validators/validators';
import {AppSettings} from '../../../models/Constants';

const DEFAULT_WAITNG_TIME = 200;

@Component({
  selector: 'app-edc-config',
  templateUrl: './edc-config.component.html',
  styleUrl: './edc-config.component.scss',
  standalone: false,
})
export class EdcConfigComponent implements OnInit {
  private readonly connectorService = inject(ConnectorService);
  private readonly userService = inject(UserService);
  private readonly modalService = inject(ModalService);
  private readonly translate = inject(TranslateService);
  private readonly toastService = inject(ToastService);

  @Input() type!: 'provider' | 'consumer';

  @ViewChild('addConfigModal', {read: TemplateRef})
  addConfigModalRef!: TemplateRef<HTMLElement>;
  @ViewChild('deleteModal', {read: TemplateRef})
  deleteModalRef!: TemplateRef<HTMLElement>;
  @ViewChild('cancelEditModal', {read: TemplateRef})
  cancelEditModalRef!: TemplateRef<HTMLElement>;

  loading = true;
  loadingSubSequence = false;
  errorMessages = [];

  private userDataSubscription?: Subscription;
  private orgId = '';
  cxEnabled = false;
  connectorConfig?: ConnectorConfig | null;
  connectorForm!: FormGroup;

  get isCxEnabled(): boolean {
    return this.cxEnabled;
  }

  set isCxEnabled(cxEnabled: boolean) {
    if (!cxEnabled && this.connectorConfig) {
      this.onDeleteClick();
    }
    this.cxEnabled = cxEnabled;
  }

  async ngOnInit() {
    this.userDataSubscription = this.userService.userDataChange.subscribe(
      async (user) => {
        if (user) {
          this.orgId = user?.organization.id || '';
          await this.loadConnectors();
          this.userDataSubscription?.unsubscribe();
        }
      },
    );
  }

  async loadConnectors(force = false) {
    this.loading = true;
    const connectorConfigs = await this.connectorService.load(
      this.orgId,
      this.type,
      force,
    );
    this.isCxEnabled = connectorConfigs.length > 0;
    if (this.isCxEnabled) {
      this.connectorConfig = await this.connectorService.loadOne(
        this.orgId,
        connectorConfigs[0].id,
      );
      this.createConnectorForm(this.connectorConfig.hostedService);
    }
    this.loading = false;
  }

  createConnectorForm(isSelfHosted: boolean) {
    const formConfig = this.createBaseConnectorFormControls(isSelfHosted);

    if (isSelfHosted) {
      Object.assign(formConfig, this.createSelfHostedFormControls());
    }

    if (isSelfHosted && this.type === 'provider') {
      Object.assign(formConfig, this.createProviderFormControls());
    }

    this.connectorForm = new FormGroup(formConfig);
  }

  private createBaseConnectorFormControls(isSelfHosted: boolean): {
    [key: string]: FormControl;
  } {
    return {
      id: new FormControl(this.connectorConfig?.id),
      type: new FormControl(this.type),
      hostedService: new FormControl(isSelfHosted),
      bpn: new FormControl(this.connectorConfig?.bpn, {
        validators: [
          Validators.required,
          Validators.maxLength(AppSettings.DEFAULT_TEXT_FIELD_LENGTH),
        ],
      }),
      organizationId: new FormControl(this.orgId || ''),
      technicalUser: new FormControl(
        this.connectorConfig?.technicalUser || null,
        {
          validators: [Validators.required],
        },
      ),
      secret: new FormControl(this.connectorConfig?.secret || null, {
        validators: [Validators.required],
      }),
      autoSyncOnComplete: new FormControl(
        this.connectorConfig?.autoSyncOnComplete ?? false,
      ),
    };
  }

  private createSelfHostedFormControls(): {[key: string]: FormControl} {
    return {
      edcUrl: new FormControl(this.connectorConfig?.edcUrl || null, {
        validators: [Validators.required, CustomValidators.urlValidator],
      }),
      edcApiKey: new FormControl(this.connectorConfig?.edcApiKey || null, {
        validators: [Validators.required],
      }),
    };
  }

  private createProviderFormControls(): {[key: string]: FormControl} {
    return {
      dtrUrl: new FormControl(this.connectorConfig?.dtrUrl || null, {
        validators: [Validators.required, CustomValidators.urlValidator],
      }),
      dtrApiKey: new FormControl(this.connectorConfig?.dtrApiKey || null, {
        validators: [Validators.required],
      }),
    };
  }

  async onAddClick(isSelfHosted: boolean) {
    this.createConnectorForm(isSelfHosted);
    await this.modalService.open({
      content: this.addConfigModalRef,
      centered: true,
      data: isSelfHosted,
      size: '600',
    });
  }

  async onSaveClick(modal: IxActiveModal) {
    this.loadingSubSequence = true;
    this.connectorForm.markAllAsTouched();
    if (this.connectorForm.valid) {
      try {
        await this.connectorService.create(
          this.orgId,
          this.connectorForm.value,
        );
        let message: string;
        if (modal.data) {
          message = await this.translate.instant(
            'organizations.settings.create_edc.success_request_message',
          );
        } else {
          message = await this.translate.instant(
            'organizations.settings.create_edc.success_message',
          );
        }
        await this.toastService.show({message, type: 'success'});
        setTimeout(() => {
          this.loadConnectors(true);
        }, DEFAULT_WAITNG_TIME);
        modal.close(true);
      } catch (error) {
        this.errorMessages = (
          error as {
            error: {message: never[]};
          }
        ).error.message;
      }
    }
    this.loadingSubSequence = false;
  }

  async onUpdateEDCClick() {
    if (this.connectorForm.valid) {
      this.loadingSubSequence = true;
      const value = this.connectorForm.value;
      delete value['hostedService'];
      await this.connectorService.update(this.orgId, this.connectorForm.value);
      const message = await this.translate.instant(
        'organizations.settings.update_edc_success_message',
      );
      await this.toastService.show({message, type: 'success'});
      this.loadingSubSequence = false;
      await this.loadConnectors();
    } else {
      this.connectorForm.markAllAsTouched();
    }
  }

  async onCancelEditClick() {
    const instance = await this.modalService.open({
      content: this.cancelEditModalRef,
      centered: true,
    });

    instance.onClose.on((shouldCancel) => {
      if (shouldCancel) {
        this.connectorForm.reset(this.connectorConfig);
      }
    });
  }

  async onDeleteClick() {
    const instance = await this.modalService.open({
      content: this.deleteModalRef,
      centered: true,
    });

    let willDelete = false;

    instance.onClose.on(() => {
      willDelete = true;
      if (this.connectorConfig) {
        this.loading = true;
        this.connectorService.deleteConnector(
          this.orgId,
          this.connectorConfig.id,
        );
        this.connectorConfig = null;
        setTimeout(() => {
          this.loadConnectors(true);
          this.loading = false;
        }, DEFAULT_WAITNG_TIME);
      }
    });

    instance.onDismiss.on(() => {
      if (!willDelete) {
        this.isCxEnabled = true;
      }
    });
  }

  getErrorMessage(controlName: string): string {
    const errors = this.connectorForm.controls[controlName]?.errors;
    if (controlName === 'bpn' && errors) {
      if (errors?.['required'])
        return 'organizations.settings.create_edc.bpn_label_error_required';
      else if (errors?.['maxlength'])
        return 'organizations.settings.create_edc.bpn_label_error_too_long';
    } else if (controlName === 'technicalUser' && errors) {
      if (errors?.['required'])
        return 'organizations.settings.create_edc.technical_user_label_error_required';
    } else if (controlName === 'secret' && errors) {
      if (errors?.['required'])
        return 'organizations.settings.create_edc.secret_label_error_required';
    } else if (controlName === 'edcUrl' && errors) {
      if (errors?.['required'])
        return (
          'organizations.settings.create_edc.url_label_error_required_' +
          this.type
        );
      else if (errors?.['maxlength'])
        return (
          'organizations.settings.create_edc.url_label_error_too_long_' +
          this.type
        );
      else if (errors?.['invalidUrl'])
        return 'organizations.settings.create_edc.url_label_invalid_url';
    } else if (controlName === 'edcApiKey' && errors) {
      if (errors?.['required'])
        return (
          'organizations.settings.create_edc.api_key_label_error_required_' +
          this.type
        );
    } else if (controlName === 'dtrUrl' && errors) {
      if (errors?.['required'])
        return 'organizations.settings.create_edc.dtr_url_label_error_required_provider';
      else if (errors?.['maxlength'])
        return 'organizations.settings.create_edc.dtr_url_label_error_too_long_provider';
      else if (errors?.['invalidUrl'])
        return 'organizations.settings.create_edc.url_label_invalid_url';
    } else if (controlName === 'dtrApiKey' && errors) {
      if (errors?.['required'])
        return 'organizations.settings.create_edc.dtr_api_key_label_error_required_provider';
    }

    return '';
  }
}
